import {
    locationCardDetailsLabel,
    locationCardMapLabel,
    address,
    phoneNumber,
    openInMaps,
} from './LocationExpandedCard.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';

import { ControlIcons } from 'owa-control-icons';
import { KeyCodes } from 'office-ui-fabric-react/lib/Utilities';
import { getBingUrl, calculateMapUrl } from 'owa-location-persona';

import getStore from '../data/store';
import { observer } from 'mobx-react';
import { Link } from 'office-ui-fabric-react/lib/Link';
import { LocationCardProps } from './LocationCardWrapperV2';
import { Image, ImageLoadState } from 'office-ui-fabric-react/lib/Image';
import { getLocationTextStyle } from 'owa-location-persona';
import { now } from 'owa-datetime';
import {
    getCapacityString,
    getCapacityPlaceholder,
    getFormatterAddress,
    getAddressPlaceholder,
    getPhonePlaceholder,
    getReservedRoomString,
    getBuildingString,
    getWebsiteString,
    getLabelString,
    hasCoordinates,
    isWorkingHourSet,
} from 'owa-location-card-v2/lib/selectors/locationCardSelectors';
import { getCompactOpeningHours } from '../utils/openingHoursUtils';
import DetailRow from 'owa-location-card-v2/lib/components/DetailRow';
import {
    Shimmer,
    ShimmerElementsGroup,
    ShimmerElementType as ElemType,
} from 'office-ui-fabric-react/lib/Shimmer';
import { logUsage } from 'owa-analytics';
import OpeningHours from './OpeningHours';

let styles = require('./LocationCard.scss');
let classNames = require('classnames/bind').bind(styles);

const MAP_WIDTH = 290;
const MAP_HEIGHT = 135;

const DETAIL_SOURCE = 'Detail';
const MAP_SOURCE = 'Map';
const MAP_LINK_SOURCE = 'MapLink';

/**
 * Opens a new tab linking to bing directions
 */
export const openDirections = (source: string) => () => {
    let place = getStore().place;
    logUsage('openDirections', [source]);

    window.open(
        getBingUrl(
            place.Coordinates ? place.Coordinates.Latitude : undefined,
            place.Coordinates ? place.Coordinates.Longitude : undefined,
            getFormatterAddress(),
            place.Name
        ),
        '_blank'
    );
};

/** Makes a call to the location */
export const call = (source: string) => () => {
    let place = getStore().place;

    logUsage('call', [source]);

    window.location.href = 'tel:' + place.PhoneNumber;
};

export interface LocationExpandedCardState {
    isMapLoaded?: boolean;
}

@observer
export default class LocationExpandedCard extends React.Component<
    LocationCardProps,
    LocationExpandedCardState
> {
    constructor(props: LocationCardProps) {
        super(props);

        this.state = {
            isMapLoaded: false,
        };
    }

    render() {
        const place = getStore().place;
        const viewState = getStore().viewState;
        const isDataLoaded = viewState.isDataLoaded;

        const imageUrl = hasCoordinates()
            ? calculateMapUrl(
                  place.Coordinates.Latitude,
                  place.Coordinates.Longitude,
                  MAP_WIDTH,
                  MAP_HEIGHT
              )
            : '';

        return (
            <span>
                <div className={styles.cardSection}>
                    <Shimmer
                        className={styles.sectionHeader}
                        isDataLoaded={isDataLoaded}
                        width={57}
                        shimmerElements={[{ type: ElemType.line, width: 57, height: 18 }]}>
                        {loc(locationCardDetailsLabel)}
                    </Shimmer>

                    <Shimmer
                        isDataLoaded={isDataLoaded}
                        width={276}
                        customElementsGroup={
                            <ShimmerElementsGroup
                                flexWrap={true}
                                shimmerElements={[
                                    { type: ElemType.line, width: 276, height: 14 },
                                    { type: ElemType.gap, width: 276, height: 14 },
                                    { type: ElemType.line, width: 276, height: 14 },
                                    { type: ElemType.gap, width: 276, height: 14 },
                                    { type: ElemType.line, width: 276, height: 14 },
                                ]}
                            />
                        }>
                        {this.renderExpandedCardBody()}
                    </Shimmer>
                </div>
                {(viewState.isLoading || imageUrl) && (
                    <div className={styles.cardSection}>
                        <Shimmer
                            className={styles.sectionHeader}
                            isDataLoaded={isDataLoaded && this.state.isMapLoaded}
                            width={57}
                            shimmerElements={[{ type: ElemType.line, width: 57, height: 18 }]}>
                            {loc(locationCardMapLabel)}
                        </Shimmer>

                        <Shimmer
                            isDataLoaded={isDataLoaded && this.state.isMapLoaded}
                            width={290}
                            shimmerElements={[{ type: ElemType.line, width: 290, height: 147 }]}>
                            {this.renderMap()}
                        </Shimmer>
                    </div>
                )}
            </span>
        );
    }

    private renderExpandedCardBody = (): JSX.Element => {
        const { place, viewState } = getStore();

        if (!place) {
            return null;
        }

        const currentTime = now();

        return (
            <>
                <DetailRow
                    text={getFormatterAddress()}
                    iconName={ControlIcons.Poi}
                    colorScheme={viewState.colorScheme}
                    ariaLabel={loc(address)}
                    placeholder={getAddressPlaceholder()}
                    onClick={openDirections(DETAIL_SOURCE)}
                />
                <DetailRow text={getBuildingString()} iconName={ControlIcons.Emi} />
                <DetailRow
                    text={place.PhoneNumber}
                    iconName={ControlIcons.Phone}
                    colorScheme={viewState.colorScheme}
                    ariaLabel={loc(phoneNumber)}
                    placeholder={getPhonePlaceholder()}
                    onClick={call(DETAIL_SOURCE)}
                />
                <DetailRow
                    text={getWebsiteString()}
                    iconName={ControlIcons.Globe}
                    colorScheme={viewState.colorScheme}
                    onClick={this.openWebsite}
                />
                <DetailRow
                    text={getLabelString()}
                    iconSize={'16px'}
                    iconName={ControlIcons.Label}
                />
                <DetailRow
                    text={getCapacityString()}
                    iconName={ControlIcons.Contact}
                    iconSize={'13px'}
                    placeholder={getCapacityPlaceholder()}
                />
                <DetailRow text={getReservedRoomString()} iconName={ControlIcons.Clock} />
                {isWorkingHourSet() && (
                    <OpeningHours
                        currentTime={currentTime}
                        openingHours={getCompactOpeningHours(
                            place.PlaceDetails.OpeningHoursSpecifications,
                            currentTime
                        )}
                    />
                )}
            </>
        );
    };

    private renderMap = (): JSX.Element => {
        const { place, viewState } = getStore();

        if (!place) {
            return null;
        }

        const imageUrl = hasCoordinates()
            ? calculateMapUrl(
                  place.Coordinates.Latitude,
                  place.Coordinates.Longitude,
                  MAP_WIDTH,
                  MAP_HEIGHT
              )
            : '';

        const linkStyle = getLocationTextStyle(viewState.colorScheme);

        return (
            <div className={styles.mapSection}>
                <Image
                    className={styles.map}
                    src={imageUrl}
                    onClick={openDirections(MAP_SOURCE)}
                    onKeyDown={this.onKeyDownAddress}
                    onLoadingStateChange={this.onMapLoadingChanged}
                />
                <Link
                    className={classNames(styles.openInMap, linkStyle)}
                    onClick={openDirections(MAP_LINK_SOURCE)}>
                    {loc(openInMaps)}
                </Link>
            </div>
        );
    };

    /** Handles a key down on the address text */
    private onKeyDownAddress = (ev: React.KeyboardEvent<any>) => {
        if (ev.which == KeyCodes.enter) {
            openDirections(MAP_SOURCE);
        }
    };

    /** Opens a new tab linking to the website of the place */
    private openWebsite = () => {
        logUsage('openWebsite');

        let place = getStore().place;
        window.open(place.PlaceDetails.BusinessUrl, '_blank');
    };

    /** Handler of the map image locading state */
    private onMapLoadingChanged = (loadState: ImageLoadState) => {
        this.setState({
            isMapLoaded: loadState === ImageLoadState.loaded,
        });
    };
}
